import { useWorkspaceText, workspaceTextFor } from '../interface-copy';
import { useChatInterfaceLocale } from '@/lib/chat-i18n';
import { useNamespaceCopy } from '@/i18n/ns';
import runtimeCopy from '@/i18n/ns/workspace-knowledge-runtime';
import { StatusPill } from '@/components/ui/kit';
import { markdownPlainText } from '../plain-text';
import { useWorkspace } from '../context';
import { agentsApi, type Agent, type AgentRun } from './api';
import { runStatusLabel, runStatusTone } from './run-status';
import { unavailableSourcesLine } from './run-card';
import { creditsText, durationShort, runWhen } from './time';
import type { Act } from './shared';

// The Runs tab (agent redesign 2026-10-09): each run says when it ran, what
// started it and how it ended, one line of what it found (the opening of its
// answer) or why it failed, and how long it took and, for the person it ran for
// or whoever manages billing, the credits it used. A row opens its run chat; a run
// still going can be watched or cancelled.

/** "Scheduled", "Run by Sara Holm", "Test run by you". */
export function runTrigger(
  run: AgentRun,
  viewerId: string | undefined,
  copy: Readonly<Record<keyof typeof runtimeCopy.en, string>> = runtimeCopy.en,
) {
  if (run.triggerType === 'schedule') return copy.runScheduled;
  const by = !run.runBy
    ? copy.formerMember
    : run.runBy.userId === viewerId
      ? copy.youLower
      : (run.runBy.name ?? copy.colleagueLower);
  return run.triggerType === 'test'
    ? copy.testRunBy.replace('{name}', by)
    : copy.runBy.replace('{name}', by);
}

// The run's one line: where it is while it is not done, why it failed, or the
// opening of its answer, after any connection it could not read.
export function runLine(
  run: AgentRun,
  locale = 'en-GB',
  copy: Readonly<Record<keyof typeof runtimeCopy.en, string>> = runtimeCopy.en,
) {
  const line =
    (
      {
        queued: copy.runQueued,
        running: copy.runRunning,
        awaiting_approval: copy.runApproval,
        cancelled: copy.runCancelled,
      } as Partial<Record<AgentRun['status'], string>>
    )[run.status] ??
    (run.status === 'failed' && run.error ? markdownPlainText(run.error) : null) ??
    (run.summary ? markdownPlainText(run.summary) : null) ??
    (run.resultId ? copy.runSavedResult : copy.runFinished);
  const merged =
    run.coalescedFires && run.coalescedFires > 1
      ? ' ' +
        workspaceTextFor(locale)(
          run.coalescedFires === 2 ? '{count} missed time merged.' : '{count} missed times merged.',
          { count: (run.coalescedFires - 1).toLocaleString(locale) },
        )
      : '';
  // A missing source leads the line, which is cut to one line in the list.
  const unreadable = unavailableSourcesLine(run.unavailableSources, run.status, locale, copy);
  return `${unreadable ? `${unreadable} ` : ''}${line}${merged}`;
}

/** "3 min · 3.1 credits"; the credits only where the server sent them. */
export function runFacts(
  run: AgentRun,
  locale = 'en-GB',
  copy: Readonly<Record<keyof typeof runtimeCopy.en, string>> = runtimeCopy.en,
) {
  return [
    run.durationMs !== null ? durationShort(run.durationMs, locale) : null,
    run.credits !== undefined ? creditsText(run.credits, locale, copy) : null,
  ]
    .filter(Boolean)
    .join(' · ');
}

export function RunsTab({
  agent,
  runs,
  busy,
  act,
  empty = 'No runs yet.',
}: {
  agent: Agent;
  runs: AgentRun[];
  busy: boolean;
  act: Act;
  empty?: string;
}) {
  const { text: uiText, rich: uiRich } = useWorkspaceText();
  const locale = useChatInterfaceLocale();
  const copy = useNamespaceCopy(runtimeCopy);
  const ws = useWorkspace();
  if (!runs.length)
    return <p className="ws-muted">{empty === 'No runs yet.' ? copy.noRuns : empty}</p>;
  return (
    <ol className="ws-agents-runs" aria-label={copy.runs}>
      {runs.map((run) => {
        const live = ['running', 'queued', 'awaiting_approval'].includes(run.status);
        const cancellable =
          live && (run.runBy?.userId === ws.user?.id || agent.isOwner || agent.manages);
        const facts = runFacts(run, locale, copy);
        return (
          <li
            key={run.id}
            className="ws-agents-run"
            data-status={run.status}
            aria-current={
              run.threadId !== null && run.threadId === ws.threadId ? 'true' : undefined
            }
          >
            <button
              className="ws-agents-run-main"
              disabled={!run.threadId}
              // The row's own words name it (when, how it started, outcome, its line).
              title={uiText('Open the run chat')}
              onClick={() => run.threadId && ws.openThread(run.threadId)}
            >
              <span className="ws-agents-run-top">
                <strong>
                  {runWhen(run.scheduledFor ?? run.createdAt, new Date(), locale, copy)}
                </strong>{' '}
                <span>· {runTrigger(run, ws.user?.id, copy)}</span>{' '}
                <StatusPill tone={runStatusTone(run.status)}>
                  {runStatusLabel(run.status, copy)}
                </StatusPill>
              </span>{' '}
              <small className="ws-agents-run-line">{runLine(run, locale, copy)}</small>
            </button>{' '}
            {facts && <span className="ws-agents-run-facts">{facts}</span>}
            {run.status === 'running' && run.threadId && (
              <button className="ws-button small" onClick={() => ws.openThread(run.threadId!)}>
                {copy.watch}
              </button>
            )}
            {cancellable && (
              <button
                className="ws-text-button"
                disabled={busy}
                onClick={() =>
                  void act(() => agentsApi.cancelRun(agent.id, run.id), `${copy.cancel}d.`)
                }
              >
                {copy.cancel}
              </button>
            )}
          </li>
        );
      })}
    </ol>
  );
}
